import appSettings from '@/appsettings.json';
import tourProjectTemplate from '@/assets/project-templates/tour-project-template.json';
import { tourSteps } from "@/context/tour/beginner-tour";
import ConductionAPI, { CurrentTos, UserLiveEvents } from "@/javascript/conduction-api";
import { Song } from "@/javascript/library-objects";
import MidiPlayer from "@/javascript/midi-player";
import { Instrument } from "@/javascript/music-objects";
import { Project, ProjectTrack, TempoChange } from "@/javascript/project-objects";
import { theme } from "@/theme";
import { IconAlertCircle, IconCheck, IconDeviceFloppy, IconLock, IconLockOpen, IconMusic, IconMusicQuestion, IconUserCheck } from "@tabler/icons-react";
import React, { createContext, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import Confetti from 'react-confetti';
import Joyride, { CallBackProps, STATUS, StoreHelpers } from "react-joyride";
import { To, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Classroom, LiveEvent, Role, User } from "../javascript/user-objects";
import { Utils } from "../javascript/utils";
import { MidiBlockCache } from "@/javascript/histomap";
import { Avatar, Button } from "@mantine/core";

export interface AuthContextType {
	user: User | null;
	token: string | null;
	songs: Song[] | null;
	project: Project | null;
	loading: boolean;
	loadingPercent: number;
	login: (usernameOrEmail: string, password: string, rememberMe: boolean, autoAgreeTOS?: boolean) => Promise<boolean>;
	guestLogin: (rememberCode: boolean) => Promise<boolean>;
	logout: () => void;
	profileAvatar: JSX.Element;
	fetchProfileAvatar: (user: User) => Promise<JSX.Element>;
	roleCheck: (roles: Role[]) => boolean;
	loginModalState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
	updateProjectName: (projectName: string) => void
	saveProject: () => Promise<void>
	openProject: (projectId?: string) => Promise<void>,
	saveStatus: "saved" | "failed" | "processing" | "no-name-warned",
	updateProject: (project: Project, callback?: () => void, signal?: AbortSignal) => void,
	lockProject: () => void,
	startBeginnerTour: () => void,
	beginnerTourStartModalOpen: boolean,
	setBeginnerTourStartModalOpen: (open: boolean) => void,
	updatePreTourChecklist: (key: string, ready: boolean) => void
	beginnerTourActive: boolean,
	currentTourStep: number,
	moveToNextTourStep: () => void,
	moveToPrevTourStep: () => void,
	jumpToTourStep: (index: number) => void,
	classroom: Classroom | null,
	participatingLiveEvents: LiveEvent[],
	administratingLiveEvents: LiveEvent[],
	playingLiveEvents: LiveEvent[],
	activeParticipatingLiveEvent: LiveEvent | null,
	activeAdministratingLiveEvent: LiveEvent | null,
	activePlayingLiveEvent: LiveEvent | null
	updateLiveEvents: () => void,
	agreeToTos: (version: string) => Promise<boolean>,
	currentTos: CurrentTos | undefined,
	fetchCurrentTos: () => Promise<void>,
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);
export const AVATAR_SIZE = 34;

let trackLiveEventInterval: number | null = null;
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const navigate = useNavigate();
	const location = useLocation();

	const loginModalState = useState(false);
	const [_, setShowLoginModal] = loginModalState;

	// Session values
	const [user, setUser] = useState<User | null>(null);
	const [songs, setSongs] = useState<Song[] | null>(null);
	const [cachedProject, setCachedProject] = useState<Project | null>(null);
	const [project, setProject] = useState<Project | null>(null);
	const [profileAvatar, setProfileAvatar] = useState<JSX.Element>(<Avatar radius="xl" size={AVATAR_SIZE} />);

	// Classroom values
	const [classroom, setClassroom] = useState<Classroom | null>(null);

	// Live event values
	const [participatingLiveEvents, setParticipatingLiveEvent] = useState<LiveEvent[] | null>(null);
	const [administratingLiveEvents, setAdministratingLiveEvent] = useState<LiveEvent[] | null>(null);
	const [playingLiveEvents, setPlayingLiveEvent] = useState<LiveEvent[] | null>(null);
	const [activeParticipatingLiveEvent, setActiveParticipatingLiveEvent] = useState<LiveEvent | null>(null);
	const [activeAdministratingLiveEvent, setActiveAdministratingLiveEvent] = useState<LiveEvent | null>(null);
	const [activePlayingLiveEvent, setActivePlayingLiveEvent] = useState<LiveEvent | null>(null);

	// Stored session values
	const getStoredUserId = () => sessionStorage.getItem("userId") || localStorage.getItem("userId");
	const getStoredToken = () => sessionStorage.getItem("authToken") || localStorage.getItem("authToken");
	const [userId, setUserId] = useState<string | null>(getStoredUserId());
	const [token, setToken] = useState<string | null>(getStoredToken());

	const [awaitingLogin, setAwaitingLogin] = useState(userId === null);
	const [loading, setLoading] = useState(false);
	const [loadingPercent, setLoadingPercent] = useState(0);
	const [saveStatus, setSaveStatus] = useState<"saved" | "failed" | "processing" | "no-name-warned">("saved");

	// Tour management
	const [currentTourStep, setCurrentTourStep] = useState<number>(0);
	const [preTourChecklist, setPreTourCheckList] = useState<{ key: string; ready: boolean }[]>([]);
	const [beginnerTourActive, setBeginnerTourActive] = useState(false);
	const [beginnerTourStartModalOpen, setBeginnerTourStartModalOpen] = useState(false);
	const tourHelpersRef = useRef<StoreHelpers | null>(null);

	const [currentTos, setCurrentTos] = useState<CurrentTos | undefined>(undefined);

	// Check if the user is logged and then check for a cached project
	const checkForCachedProject = useCallback(() => {
		if (user === null) {
			return;
		}
		const cachedProjectString = localStorage.getItem("cachedProject");
		if (cachedProjectString) {
			const cachedProjectJson = JSON.parse(cachedProjectString!);
			const project = Project.restoreFromJson(cachedProjectJson, user);
			if (project !== null) {
				setCachedProject(project);
			}
		}
	}, [user]);

	// Ask the user if they want to restore the cached project
	useEffect(() => {
		if (cachedProject !== null) {
			const projectName = cachedProject.getName();
			const icon = <IconMusicQuestion size={16} />;
			const responseNode = (
				<Button
					size="xs"
					variant="outline"
					onClick={async () => {
						setProject(cachedProject);
						setCachedProject(null);
						localStorage.removeItem("cachedProject");
						const icon = <IconCheck size={16} />;
						Utils.updateNotification(
							"cached-project",
							`Project ${projectName !== "" ? `"${projectName}" ` : ""}Restored!`,
							"We have restored your project as a copy so that the original is not overwritten. Make sure to save your work.",
							"green",
							icon,
							true,
							5000
						);
					}}
					sx={{ marginLeft: 10 }}
				>
					Recover project{projectName !== "" ? ` "${projectName}"` : ""}
				</Button>
			);
			Utils.displayNotification(
				"Unsaved Project Found",
				responseNode,
				"blue",
				icon,
				true,
				false,
				"cached-project",
				() => {
					// This is called when the notification is closed. We can clear the cached project here if the user doesn't want to restore it.
					setCachedProject(null);
					localStorage.removeItem("cachedProject");
				}
			);
		}
	}, [cachedProject]);

	// Fetch the current user based on stored token
	const fetchUser = async () => {
		if (!userId || !token) {
			setLoading(false);
			setAwaitingLogin(true);
			return;
		}
		setLoading(true);
		setAwaitingLogin(true);

		if (userId === "guest") {
			setUser(User.getGuestUser());
			setAwaitingLogin(false);
			setShowLoginModal(false);
			return;
		}

		try {
			const user = await ConductionAPI.getUser(userId, token)
			if (user != undefined) {
				setShowLoginModal(false);
				setUser(user);
			} else {
				logout(); // Clear token if invalid
			}
		} catch (error) {
			console.error("Error fetching user:", error);
			logout(); // Clear token if invalid
		} finally {
			setShowLoginModal(false);
			setAwaitingLogin(false);
		}
	};

	const fetchClassroom: () => Promise<boolean> = useCallback(async () => {
		if (user === null || token === null || user.roles.includes("Guest")) {
			// Classroom can only be fetched after login
			return false;
		}

		try {
			const loadedClassroom: Classroom | null = await ConductionAPI.getUserClassroom(user.id, token);
			if (loadedClassroom === null) {
				return false;
			} else {
				setClassroom(loadedClassroom);
				return true;
			}
		} catch (error) {
			console.error('Error fetching classroom:', error);
			Utils.displayNotification(
				'Library Load Failure',
				'Something went wrong when loading the library. Please try again later.',
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
			return false;
		}
	}, [user, token]);

	const fetchLiveEvents: () => Promise<boolean> = useCallback(async () => {
		if (user === null || token === null || user.roles.includes("Guest")) {
			// Live events can only be fetched after login
			return false;
		}

		try {
			const loadedLiveEvents: UserLiveEvents = await ConductionAPI.getUserLiveEvents(user.id, token);
			setParticipatingLiveEvent(loadedLiveEvents.participatingLiveEvents);
			setAdministratingLiveEvent(loadedLiveEvents.administratingLiveEvents);
			setPlayingLiveEvent(loadedLiveEvents.playingLiveEvents);

			// Check if any of the live events are active (the current time is between the start and end time)
			const currentTime = new Date();
			const activeParticipating = loadedLiveEvents.participatingLiveEvents.find((liveEvent) => {
				return liveEvent.startTime <= currentTime && currentTime <= liveEvent.endTime;
			});
			const activeAdministrating = loadedLiveEvents.administratingLiveEvents.find((liveEvent) => {
				return liveEvent.startTime <= currentTime && currentTime <= liveEvent.endTime;
			});
			const activePlaying = loadedLiveEvents.playingLiveEvents.find((liveEvent) => {
				return liveEvent.startTime <= currentTime && currentTime <= liveEvent.endTime;
			});

			setActiveParticipatingLiveEvent(activeParticipating || null);
			setActiveAdministratingLiveEvent(activeAdministrating || null);
			setActivePlayingLiveEvent(activePlaying || null);

			// If there are any active live events, we will return true to indicate that the user is in a live event
			if (activeParticipating || activeAdministrating || activePlaying) {
				// Check if the currently open project should be unlocked
				if (project !== null && project.isLocked()) {
					const liveEvent = activeParticipating || activeAdministrating || activePlaying;
					if (liveEvent !== undefined) {
						const projectInQueue = liveEvent.getProjectPerformanceQueue()?.find((p) => p.getId() === project.getId());
						const projectInReview = liveEvent.getProjectsReadyForReview()?.find((p) => p.getId() === project.getId());
						if (projectInQueue === undefined && projectInReview === undefined) {
							Utils.displayNotification(
								"Your project has been unlocked.",
								"It can again be edited and submitted for review.",
								"blue",
								<IconLockOpen size={16} />,
								true,
								3000
							);
							project.unlock();
							setProject(project);
						}
					}
				}

				Utils.updateNotification(
					"live-event-fetch-error",
					"Event updates loaded!",
					undefined,
					"green",
					<IconAlertCircle size={16} />,
					true,
					400
				);

				return true;
			} else {
				// If no live events are active, we don't need to track for them anymore
				if (trackLiveEventInterval) {
					clearInterval(trackLiveEventInterval);
				}
				return false;
			}
		} catch (error) {
			console.error('Error fetching live events:', error);
			Utils.displayNotification(
				"An error occurred while loading your event updates.",
				"Please try again later.",
				"red",
				<IconAlertCircle size={16} />,
				true,
				false,
				"live-event-fetch-error"
			);
			return false;
		}
	}, [user, token, project]);

	const loadInstruments = useCallback(async (instruments: Instrument[]) => {
		if (user === null || token === null) {
			// Instruments can only be fetched after login
			return;
		}

		setLoading(true); // We use the loading screen for this
		try {
			await Instrument.updateAllInstrumentOptions(
				instruments,
				(loadedInstrumentCount: number, totalInstrumentCount) =>
					setLoadingPercent(loadedInstrumentCount / totalInstrumentCount * 100)
			);
		} catch (error) {
			console.error('Error fetching instruments:', error);
			Utils.displayNotification(
				'Instrument Load Failure',
				'Something went wrong when loading instruments. Please try again later.',
				"red",
				<IconAlertCircle size={16} />,
				true,
				false,
				"instrument-load-failure"
			);
		} finally {
			setLoading(false);
			setLoadingPercent(100);
		}
	}, [user, token, setLoading, setLoadingPercent]);

	const fetchUserSongsAndInstruments = useCallback(async () => {
		if (user === null || token === null || !loading) {
			// Instruments and songs can only be fetched after login
			return;
		}

		// Get instruments
		ConductionAPI.getInstruments().then((instruments: Instrument[]) => {
			loadInstruments(instruments);
		}).catch((error: any) => {
			Utils.displayNotification(
				"Failed to load instruments",
				"An error occurred while loading instruments.",
				"red",
				<IconAlertCircle size={16} />,
				true,
				false,
				"instrument-load-failure"
			);
			console.error("Failed to load instruments:", error);
		});

		try {
			const loadedSongs = user.roles.includes("Guest")
				? await ConductionAPI.getGuestSongs(token)
				: await ConductionAPI.getUserSongs(token);

			if (!loadedSongs) {
				return;
			}
			setSongs(loadedSongs.sort((a, b) => b.uploadDate.getTime() - a.uploadDate.getTime())); // Newest first

			// Beginner tour (start)
			if (user !== null && !user.roles.includes("ConductionAdmin") && !user.roles.includes("Instrumentalist") && !user.roles.includes("Transcriber") && user.completedBeginnerTutorial === false && loadedSongs.length > 0) {
				updatePreTourChecklist("library-ready", true);
			}
		} catch (error) {
			console.error('Error fetching songs:', error);
			Utils.displayNotification(
				'Library Load Failure',
				'Something went wrong when loading the library. Please try again later.',
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
		}
	}, [user, token, loading]);

	const loadSongsAndInstruments = useCallback(async () => {
		if (user === null || token === null) {
			// Don't process if the user is not logged in
			return;
		}

		// Prioritize loading live events over classroom
		const eventIsActive: boolean = await fetchLiveEvents();
		if (!eventIsActive) {
			// If no live events are active, we will load the songs and instruments from the classroom
			const classroomFound = await fetchClassroom();
			if (!classroomFound) {
				// If no classroom is found, we will load the songs and instruments from the user
				fetchUserSongsAndInstruments();
			}
		}
	}, [user, token, fetchLiveEvents, fetchClassroom, fetchUserSongsAndInstruments]);

	const trackLiveEvents = useCallback(() => {
		if (user === null || token === null) {
			// Don't process if the user is not logged in
			return;
		}

		// Check live events every 10 seconds
		if (trackLiveEventInterval) {
			clearInterval(trackLiveEventInterval);
		}
		trackLiveEventInterval = setInterval(fetchLiveEvents, 10000);
		return () => (trackLiveEventInterval && clearInterval(trackLiveEventInterval));
	}, [user, token, fetchLiveEvents]);

	// Load songs when the user changes
	useEffect(() => {
		loadSongsAndInstruments();
		trackLiveEvents();
		fetchMyProfileAvatar();
	}, [user, token, trackLiveEvents, loadSongsAndInstruments]);

	// Load user when token changes
	useEffect(() => {
		if (userId === null) {
			setAwaitingLogin(true);
			setShowLoginModal(true);
		}
		updatePreTourChecklist("library-ready", false);
		updatePreTourChecklist("loading-screen-done", false);
		fetchUser();
	}, [userId, token]);

	// Login function for regular user
	const login = async (usernameOrEmail: string, password: string, rememberMe: boolean, autoAgreeTOS?: boolean): Promise<boolean> => {
		const notificaitonId = Utils.displayNotification("Attempting Login...", "This may take a few seconds.", "blue", "loading");
		try {
			const loginResults = await ConductionAPI.login(usernameOrEmail, password, rememberMe);
			if (loginResults === null) {
				const icon = <IconAlertCircle size={16} />;
				Utils.updateNotification(notificaitonId, "Login Failed", "The provided username or password was incorrect.", "red", icon);
				return false;
			}
			const loggedInUser = loginResults.user;
			const loggedInToken = loginResults.token;

			setUserId(loggedInUser.id)
			setToken(loggedInToken);

			// Store token based on rememberMe preference
			if (rememberMe) {
				localStorage.setItem("userId", loggedInUser.id)
				localStorage.setItem("authToken", loggedInToken);
			} else {
				sessionStorage.setItem("userId", loggedInUser.id);
				sessionStorage.setItem("authToken", loggedInToken);
			}

			if (autoAgreeTOS) {
				if (currentTos != null) {
					const locale = navigator.language || 'en-US';
					await ConductionAPI.acceptTos(currentTos.version, loggedInToken, locale);
				}
			}

			const icon = <IconUserCheck size={16} />;
			Utils.updateNotification(notificaitonId, "Login Successful", "Welcome back to Conduction!", "green", icon);
			return true;
		} catch (error) {
			const icon = <IconAlertCircle size={16} />;
			Utils.updateNotification(notificaitonId, "App Failure", "We are sorry, but an error has occurred during the login process. Please try again later.", "red", icon, true, false);
			console.error("Login failed:", error);
			return false;
		}
	};

	// Login function for guests
	const guestLogin = async (rememberCode: boolean): Promise<boolean> => {
		try {
			const loginResults = await ConductionAPI.guestLogin();
			if (loginResults === null) {
				const icon = <IconAlertCircle size={16} />;
				Utils.displayNotification("Guest Login Failed", "The provided access code was incorrect.", "red", icon);
				return false;
			}
			const loggedInUser = loginResults.user;
			const loggedInToken = loginResults.token;

			setUserId(loggedInUser.id)
			setToken(loggedInToken);

			// Store token based on rememberMe preference
			if (rememberCode) {
				localStorage.setItem("userId", loggedInUser.id)
				localStorage.setItem("authToken", loggedInToken);
			} else {
				sessionStorage.setItem("authToken", loggedInUser.id);
				sessionStorage.setItem("authToken", loggedInToken);
			}

			return true;
		} catch (error) {
			const icon = <IconAlertCircle size={16} />;
			Utils.displayNotification("App Failure", "We are sorry, but an error has occurred during the authentication process. Please try again later.", "red", icon, true, false);
			console.error("Login failed:", error);
			return false;
		}
	};

	const agreeToTos = useCallback(async (version: string) => {
		if (!token) {
			throw new Error("User must be logged in to agree to the Terms of Service.");
		}

		try {
			const locale = navigator.language || 'en-US';
			await ConductionAPI.acceptTos(version, token, locale);
			setUser(prev => {
				if (!prev) return null;
				const updated = Object.create(Object.getPrototypeOf(prev));
				Object.assign(updated, prev, { agreedToCurrentTos: true });
				return updated;
			});

			return true;
		} catch (error) {
			Utils.displayNotification("TOS Acceptance Failed", "There was an error recording your acceptance of the Terms of Service. Please try again later.", "red", <IconAlertCircle size={16} />, true, false);
			console.error("Error accepting Terms of Service:", error);

			return false;
		}
	}, [token]);

	const fetchingTosRef = useRef<boolean>(false);
	const fetchCurrentTos = useCallback(async () => {
		if (fetchingTosRef.current || currentTos !== undefined) return;
		fetchingTosRef.current = true;

		try {
			const currentTos = await ConductionAPI.getCurrentTos();
			setCurrentTos(currentTos ?? undefined);
		} catch (error) {
			console.error("Error fetching current Terms of Service:", error);
		} finally {
			fetchingTosRef.current = false;
		}
	}, [userId, token]);

	// Logout function
	const logout = async () => {
		MidiPlayer.stop();

		// Only clear the project if the user is not a guest because they may want to keep working on it once they log in
		if (user === null || !user.roles.includes("Guest")) {
			setProject(null);
		}

		setAwaitingLogin(true);
		setUser(null);
		setUserId(null);
		setToken(null);
		setSongs(null);
		setClassroom(null);
		setParticipatingLiveEvent(null);
		setAdministratingLiveEvent(null);
		setPlayingLiveEvent(null);
		setActiveParticipatingLiveEvent(null);
		setActiveAdministratingLiveEvent(null);
		setActivePlayingLiveEvent(null);

		// Clear the cached-project notification if it is present
		Utils.updateNotification("cached-project", "", "undefined", "blue", <></>, false, 1);

		localStorage.removeItem("userId");
		sessionStorage.removeItem("userId");
		localStorage.removeItem("authToken");
		sessionStorage.removeItem("authToken");

		updatePreTourChecklist("tour-not-completed-this-session", true); // Reset the potential for a tour this session

		setShowLoginModal(true);
	};

	const roleCheck = (roles: Role[]): boolean => {
		if (user !== null) {
			let userHasAccess = false;
			roles.forEach((role) => {
				if (user.roles.includes(role)) {
					userHasAccess = true;
				}
			});
			// If the user has access, just return.
			if (userHasAccess) return true;

			// The user does not have access, so let the know and let them log in.
			const icon = <IconAlertCircle size={16} />;
			Utils.displayNotification("Access Denied", "Please log into an account that has access to the desired resource.", "red", icon);
			return false;
		}
		return false;
	}

	const fetchMyProfileAvatar = useCallback(async () => {
		if (!user?.id || !token) {
			return;
		}

		setProfileAvatar(await fetchProfileAvatar(user));
	}, [user?.id, token]);

	const fetchProfileAvatar = useCallback(async (user: User) => {
		const getAvatarFallback = () => {
			const initials = (user.firstName?.[0] ?? user.username?.[0] ?? "").toUpperCase() + (user.lastName?.[0] ?? "").toUpperCase();
			const color = Utils.getColorFromString(user.id);
			return (
				<Avatar
					alt={user.username}
					radius="xl"
					size={AVATAR_SIZE}
					name={initials}
					color="initials"
					allowedInitialsColors={[color]}
					key={user.id}
				/>
			);
		};

		if (user.uploadedProfileImage) {
			try {
				const image = await fetchProfileAvatarImage(user.id);
				return <Avatar src={image} alt={user.username} radius="xl" size={AVATAR_SIZE} key={user.id} />;
			} catch (error) {
				console.error('Error fetching profile image:', error);
				return getAvatarFallback();
			}
		} else {
			return getAvatarFallback();
		}
	}, []);

	const fetchProfileAvatarImage = useCallback(async (userId: string) => {
		if (!userId) {
			return null;
		}
		try {
			const timestamp = new Date().getTime();
			const imageUrl = `${appSettings.S3BucketBaseUrl}ProfileImages/${userId}.jpg?cb=${timestamp}`;

			const res = await fetch(imageUrl, { method: "GET", cache: "no-cache" });
			if (res.ok) {
				return imageUrl;
			} else {
				return null; // Image not found
			}
		} catch (error) {
			return null;
		}
	}, []);

	// ----------------------
	// Live event management
	// ----------------------

	useEffect(() => {
		// If there is an active live event that the user is participating in or administrating, we will load the instruments and songs for it (unless the instrument list is already loaded)
		if (token === null || (activeParticipatingLiveEvent === null && activeAdministratingLiveEvent === null && activePlayingLiveEvent === null) || !loading) {
			return;
		}

		const liveEventId = activeParticipatingLiveEvent !== null ? activeParticipatingLiveEvent.id : activeAdministratingLiveEvent !== null ? activeAdministratingLiveEvent.id : activePlayingLiveEvent!.id;

		// Load the live event instruments
		Utils.displayNotification(
			"Loading Live Event Instrument Library",
			undefined,
			"blue",
			"loading",
			false,
			false,
			"live-event-instrument-load"
		);
		ConductionAPI.getLiveEventInstruments(liveEventId, token).then((instruments: Instrument[]) => {
			loadInstruments(instruments);
			Utils.updateNotification(
				"live-event-instrument-load",
				"Event instruments loaded",
				undefined,
				"green",
				<IconCheck size={16} />,
				true,
				400
			);
		}).catch((error: any) => {
			Utils.displayNotification(
				"Failed to load event instruments",
				"An error occurred while loading the live event instruments.",
				"red",
				<IconAlertCircle size={16} />,
				true,
				false,
				"live-event-instrument-load"
			);
			console.error("Failed to load live event instruments:", error);
		});

		// Load the live event songs
		Utils.displayNotification(
			"Loading Live Event Song Library",
			undefined,
			"blue",
			"loading",
			false,
			false,
			"live-event-song-load",
		);

		ConductionAPI.getLiveEventSongs(liveEventId, token).then((songs: Song[]) => {
			setSongs(songs);
			Utils.updateNotification(
				"live-event-song-load",
				"Live Event Songs Loaded",
				undefined,
				"green",
				<IconCheck size={16} />,
				true,
				1000
			);

			// Beginner tour (start)
			if (
				user !== null
				&& activeParticipatingLiveEvent !== null
				&& !user.roles.includes("ConductionAdmin")
				&& !user.roles.includes("Instrumentalist")
				&& !user.roles.includes("Transcriber")
				&& user.completedBeginnerTutorial === false
				&& songs.length > 0
			) {
				updatePreTourChecklist("library-ready", true);
			}
		}).catch((error: any) => {
			Utils.updateNotification(
				"live-event-song-load",
				"Failed to load live event songs",
				"An error occurred while loading the live event songs.",
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
			console.error("Failed to load live event songs:", error);
		});
	}, [token, activeParticipatingLiveEvent, activeAdministratingLiveEvent, activePlayingLiveEvent, loading]);

	const updateLiveEvents = useCallback(() => {
		fetchLiveEvents();
	}, [user, token, fetchLiveEvents]);

	// ----------------------
	// Classroom management
	// ----------------------

	useEffect(() => {
		// If there is an active classroom, we will load the instruments and songs for it
		if (token === null || classroom === null || !loading) {
			return;
		}

		// Load the classroom instruments
		Utils.displayNotification(
			"Loading your classroom instruments",
			undefined,
			"blue",
			"loading",
			false,
			false,
			"classroom-instrument-load"
		);
		ConductionAPI.getClassroomInstruments(classroom.id, token).then((instruments: Instrument[]) => {
			loadInstruments(instruments);
			Utils.updateNotification(
				"classroom-instrument-load",
				"Classroom instruments loaded",
				undefined,
				"green",
				<IconCheck size={16} />,
				true,
				500
			);
		}).catch((error: any) => {
			Utils.updateNotification(
				"classroom-instrument-load",
				"Failed to load classroom instruments",
				"An error occurred while loading the classroom instruments.",
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
			console.error("Failed to load classroom instruments:", error);
		});

		// Load the classroom songs
		const songsNotificationId = Utils.displayNotification(
			"Loading Classroom Song Library",
			undefined,
			"blue",
			"loading"
		);
		ConductionAPI.getClassroomSongs(classroom.id, token).then((songs: Song[]) => {
			setSongs(songs);
			Utils.updateNotification(
				songsNotificationId,
				"Classroom Songs Loaded",
				undefined,
				"green",
				<IconCheck size={16} />,
				true,
				1000
			);

			// Beginner tour (start)
			if (user !== null && !user.roles.includes("ConductionAdmin") && !user.roles.includes("Instrumentalist") && !user.roles.includes("Transcriber") && user.completedBeginnerTutorial === false && songs.length > 0) {
				updatePreTourChecklist("library-ready", true);
			}
		}).catch((error: any) => {
			Utils.updateNotification(
				songsNotificationId,
				"Failed to load classroom songs",
				"An error occurred while loading the classroom songs.",
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
			console.error("Failed to load classroom songs:", error);
		});
	}, [token, classroom]);

	// ----------------------
	// Session project management
	// ----------------------

	const [allowLoadProjectFromSearchParams, setAllowLoadProjectFromSearchParams] = useState(true);
	const openProject = useCallback(async (projectId?: string) => {
		if (projectId != undefined) {
			await loadProject(projectId);

			// Update the URL search params to reflect the opened project for sharing purposes
			setAllowLoadProjectFromSearchParams(false);
			const params = new URLSearchParams(location.search);
			params.set("projectId", projectId); // Add or update the search param
			const to: To = { search: params.toString(), pathname: "/" };
			navigate(to, { replace: true });
		} else {
			navigate("/", { replace: true });
		}
	}, [location.search, navigate]);

	const [searchParams] = useSearchParams();

	const loadProjectFromSearchParams = useCallback(async () => {
		if (user === null) {
			setShowLoginModal(true);
		} else {
			const projectId = searchParams.get("projectId")
			if (projectId !== null) {
				loadProject(projectId, true);
			} else {
				if (project == null || !project.getUser()?.roles.includes("Guest")) {
					const project = await Project.createNew(user);
					setProject(project);
				} else {
					// Change the owner of the guest project to the logged in user
					const updatedProject = project.createCopy(true);
					updatedProject.changeOwner(user);
					setProject(updatedProject);
				}
			}
			checkForCachedProject();
		}
	}, [user, searchParams, project]);

	useEffect(() => {
		if (!awaitingLogin && !loading && allowLoadProjectFromSearchParams) {
			loadProjectFromSearchParams();
		}
	}, [loading, searchParams, user]);

	const loadProject = useCallback(async (projectId: string, withNotification: boolean = false) => {
		if (token == null) {
			setShowLoginModal(true);
			return;
		}

		if (projectId === project?.getId()) {
			// Project is already loaded
			return;
		}

		const notificationId = withNotification ? Utils.displayNotification(
			"Loading Project...",
			undefined,
			"blue",
			"loading"
		) : null;

		try {
			const loadedProject = await ConductionAPI.loadProject(projectId, token);
			MidiBlockCache.clear(); // Clear canvas cache from timeline items

			const liveEvent = activeParticipatingLiveEvent ?? activeAdministratingLiveEvent ?? activePlayingLiveEvent;
			if (liveEvent !== null) {
				// if the project is in the live event's performance queue or is submitted for review, we will lock the project
				if (liveEvent.getProjectPerformanceQueue()?.find((p) => p.getId() === loadedProject.getId()) != null || liveEvent.getProjectsReadyForReview()?.find((p) => p.getId() === loadedProject.getId()) != null) {
					loadedProject.lock();
				}
			}

			setProject(loadedProject);

			MidiPlayer.setTempo(loadedProject.getLatestTempo(0));

			if (notificationId !== null) {
				Utils.updateNotification(
					notificationId,
					`Project "${loadedProject.getName()}" Loaded`,
					undefined,
					"green",
					<IconCheck size={16} />
				);
			}
		} catch {
			const notificationProps = {
				title: "Access Denied",
				message: "Please log into an account that has access to this project to continue.",
				color: "red",
				icon: <IconAlertCircle size={16} />,
				withCloseButton: true,
				autoClose: false
			};

			if (notificationId !== null) {
				Utils.updateNotification(
					notificationId,
					notificationProps.title,
					notificationProps.message,
					notificationProps.color,
					notificationProps.icon,
					notificationProps.withCloseButton,
					notificationProps.autoClose
				);
			} else {
				Utils.displayNotification(
					notificationProps.title,
					notificationProps.message,
					notificationProps.color,
					notificationProps.icon,
					notificationProps.withCloseButton,
					notificationProps.autoClose
				);
			}
		}
	}, [activeParticipatingLiveEvent, activeAdministratingLiveEvent, activePlayingLiveEvent, token]);

	const updateProjectName = async (projectName: string) => {
		if (!project) return;

		if (user === null) {
			setShowLoginModal(true);
		} else {
			if (project.isLocked()) {
				const lockIcon = <IconLock size={16} />;
				Utils.displayNotification("Project is locked!", "This project is locked and cannot be edited right now.", "gray", lockIcon);
				return;
			}

			if (!project.userCanEdit(user)) {
				const copiedProject = project.createCopy(true);
				setProject(copiedProject);
				const lockIcon = <IconLock size={16} />;
				Utils.displayNotification("Permission problem!", "You are not the owner of this project and do not have permission to edit.", "gray", lockIcon);
				return;
			}

			const copiedProject = project.createCopy(true);
			copiedProject.setName(projectName);
			copiedProject.setDirty(true);
			setProject(copiedProject);
		}
	};

	const saveProject: () => Promise<void> = useCallback(async () => {
		if (!project) return;

		if (user !== null && token !== null) {
			if (token === null) {
				throw new Error("You can only save once you have logged in.");
			}

			if (project.isLocked()) {
				const lockIcon = <IconLock size={16} />;
				Utils.displayNotification("Project is locked!", "This project is locked and cannot be edited right now.", "red", lockIcon);
				return;
			}

			if (saveStatus === "processing") {
				const lockIcon = <IconLock size={16} />;
				Utils.displayNotification("A save is already being processed.", undefined, "gray", lockIcon);
				return;
			}

			const userHasEditAccess = await project.userCanEdit(user);
			if (!userHasEditAccess) {
				const lockIcon = <IconLock size={16} />;
				Utils.displayNotification("Permission problem!", "You are not the owner of this project and do not have permission to edit.", "gray", lockIcon);
				return;
			}

			if (!project.hasItems()) {
				const icon = <IconMusic size={16} />;
				Utils.displayNotification("Try adding some music to the timeline before saving!", undefined, "red", icon);

				return;
			}

			setSaveStatus("processing");

			project.validateAndFix(); // Validate and fix the project before saving

			project.setName(project.getName().trim()); // Trim the name and double check the client side sanitization before saving
			if (project.isNewProject()) {
				await ConductionAPI.createProject(project, token).then((newId: string | null) => {
					if (newId != null) {
						// If the user has a autosave or save error messages, clear them now
						Utils.updateNotification("autosave-failed", "Project saved sucessfully.", undefined, "green", <IconDeviceFloppy size={16} />, true, 4000);
						Utils.updateNotification("save-failed", "Project saved sucessfully.", undefined, "green", <IconDeviceFloppy size={16} />, true, 4000);

						// Remove unnecesary cached project
						localStorage.removeItem("cachedProject");

						setSaveStatus("saved");
						openProject(newId);
					} else {
						setSaveStatus("failed");
						const alertIcon = <IconAlertCircle size={16} />;
						try {
							localStorage.setItem("cachedProject", JSON.stringify(project.toRestorableJson()));
							Utils.displayNotification("Something went wrong.", "Please check your internet connection and try saving again later. If the problem remains, try refreshing the app and your project should be restorable.", "red", alertIcon, true, false, "save-failed");
						} catch (error) {
							console.error("Error saving project to local storage:", error);
							Utils.displayNotification("Something went wrong.", "Please check your internet connection and try saving again later. ", "red", alertIcon, true, false, "save-failed");
						}
					}
				});
			} else {
				await ConductionAPI.saveProject(project, token).then((result: boolean) => {
					if (result) {
						// If the user has a autosave or save error messages, clear them now
						Utils.updateNotification("autosave-failed", "Project saved sucessfully.", undefined, "green", <IconDeviceFloppy size={16} />, true, 4000);
						Utils.updateNotification("save-failed", "Project saved sucessfully.", undefined, "green", <IconDeviceFloppy size={16} />, true, 4000);

						// Remove unnecesary cached project
						localStorage.removeItem("cachedProject");

						const projectCopy = project.createCopy();
						projectCopy.setDirty(false);
						setSaveStatus("saved");
						setProject(projectCopy);
					} else {
						setSaveStatus("failed");
						const alertIcon = <IconAlertCircle size={16} />;
						try {
							localStorage.setItem("cachedProject", JSON.stringify(project.toRestorableJson()));
							Utils.displayNotification("Save failed.", "Please check your internet connection and try saving again later. If the problem remains, try refreshing the app and your project should be restorable.", "red", alertIcon, true, false, "save-failed");
						} catch (error) {
							console.error("Error saving project to local storage:", error);
							Utils.displayNotification("Something went wrong.", "Please check your internet connection and try saving again later.", "red", alertIcon, true, false, "save-failed");
						}
					}
				});
			}
		}
	}, [project, user, token, saveStatus, beginnerTourActive, currentTourStep, openProject]);

	const updateProject = async (newProject: Project, callback?: () => void, signal?: AbortSignal) => {
		if (newProject.isLocked()) {
			const lockIcon = <IconLock size={16} />;
			Utils.displayNotification("Project is locked!", "This project is locked and cannot be edited right now.", "gray", lockIcon, true, 3000, "project-locked");
			return;
		}

		if (user !== null) {
			if (!newProject.userCanEdit(user)) {
				const lockIcon = <IconLock size={16} />;
				Utils.displayNotification(
					"Permission problem!",
					"You are not the owner of this project and do not have permission to edit.",
					"gray",
					lockIcon,
					true,
					4000,
					// This method can get called very fast. We avoid spamming the user with notifcations by using a default ID.
					"permission-problem"
				);
				return;
			}
			MidiPlayer.stop();
			newProject.setDirty(true);
			if (signal !== undefined && signal.aborted) {
				return;
			}

			newProject.validateAndFix();

			setProject(newProject);
			if (callback) callback();
		} else {
			setShowLoginModal(true);
		}
	};

	const lockProject = () => {
		if (project !== null) {
			project.lock()
			setProject(project.createCopy());
		}
	}

	const handleBeforeUnload = useCallback((e: BeforeUnloadEvent) => {
		if (project === null || user === null || user.roles.includes("Guest") || !project.hasItems() || !project.userCanEdit(user) || !project.isDirty()) return;

		// If the project was not saved, we can cache it to local storage
		try {
			localStorage.setItem("cachedProject", JSON.stringify(project.toRestorableJson()));
		} catch (error) {
			console.error("Error saving project to local storage:", error);
		}

		// This line prevents the default action and triggers the confirmation dialog
		e.preventDefault();

		// Setting returnValue activates the browser's built-in warning.
		// Custom messages set here are generally ignored by modern browsers.
		e.returnValue = 'You might have unsaved changes.';

		// Returning a value is required by some browsers
		return 'You might have unsaved changes.';
	}, [project, user]);

	// Autosave will return false if the project is unnamed or fails to save, and true if it is saved or does not needed to be saved
	const autosave = useCallback<() => Promise<boolean>>(async () => {
		if (user === null || token === null || project === null || user.roles.includes("Guest") || !project.hasItems() || !project.isDirty() || beginnerTourActive || saveStatus === "processing") {
			return true;
		}

		if (token === null) {
			console.error("You can only save once you have logged in.");
			return true;
		}

		if (project.isLocked()) {
			return true;
		}

		const userHasEditAccess = await project.userCanEdit(user);
		if (!userHasEditAccess) {
			return true;
		}

		project.setName(project.getName().trim()); // Trim the name and double check the client side sanitization before saving
		if (project.getName() === "") {
			if (saveStatus !== "no-name-warned") {
				const icon = <IconAlertCircle size={16} />;
				Utils.displayNotification(
					"Don't forget to give your project a name so you can save your work!",
					undefined,
					"blue",
					icon,
					true,
					false,
					"project-name-empty",
				);
				setSaveStatus("no-name-warned");
			}
			return false;
		}

		setSaveStatus("processing");

		project.validateAndFix(); // Validate and fix the project before saving

		if (project.isNewProject()) {
			await ConductionAPI.createProject(project, token).then((newId: string | null) => {
				if (newId != null) {
					// If the user has a autosave or save error messages, clear them now
					Utils.updateNotification("autosave-failed", "Project saved sucessfully.", undefined, "green", <IconDeviceFloppy size={16} />, true, 4000);
					Utils.updateNotification("save-failed", "Project saved sucessfully.", undefined, "green", <IconDeviceFloppy size={16} />, true, 4000);

					// Remove unnecesary cached project
					localStorage.removeItem("cachedProject");

					setSaveStatus("saved");
					openProject(newId);
					return true;
				} else {
					setSaveStatus("failed");
					const alertIcon = <IconAlertCircle size={16} />;
					try {
						localStorage.setItem("cachedProject", JSON.stringify(project.toRestorableJson()));
						Utils.displayNotification("Autosave failed.", "Please check your internet connection. If the problem persists, try refreshing the app and your project should be restorable.", "red", alertIcon, true, false, "autosave-failed");
					} catch (error) {
						console.error("Error saving project to local storage:", error);
						Utils.displayNotification("Autosave failed.", "Please check your internet connection.", "red", alertIcon, true, false, "autosave-failed");
					}
					return false;
				}
			});
		}
		else {
			await ConductionAPI.saveProject(project, token).then((result: boolean) => {
				if (result) {
					// If the user has a autosave or save error messages, clear them now
					Utils.updateNotification("autosave-failed", "Project saved sucessfully.", undefined, "green", <IconDeviceFloppy size={16} />, true, 4000);
					Utils.updateNotification("save-failed", "Project saved sucessfully.", undefined, "green", <IconDeviceFloppy size={16} />, true, 4000);

					// Remove unnecesary cached project
					localStorage.removeItem("cachedProject");

					const projectCopy = project.createCopy();
					projectCopy.setDirty(false);
					setProject(projectCopy);
					setSaveStatus("saved");
					return true;
				} else {
					setSaveStatus("failed");
					const alertIcon = <IconAlertCircle size={16} />;
					try {
						localStorage.setItem("cachedProject", JSON.stringify(project.toRestorableJson()));
						Utils.displayNotification("Autosave failed.", "Please check your internet connection. If the problem persists, try refreshing the app and your project should be restorable.", "red", alertIcon, true, false, "autosave-failed");
					} catch (error) {
						console.error("Error saving project to local storage:", error);
						Utils.displayNotification("Autosave failed.", "Please check your internet connection.", "red", alertIcon, true, false, "autosave-failed");
					}
					return false;
				}
			});
		}
		// This should never be reached, but just in case we return false to indicate that the project was not saved
		return false;
	}, [project, user, beginnerTourActive, token, saveStatus, openProject]);

	let autosaveInterval: number | undefined;
	useEffect(() => {
		autosaveInterval = setInterval(() => {
			autosave();
		}, 10000); // Autosave 10 seconds after the last edit

		return () => clearInterval(autosaveInterval);
	}, [autosave]);

	useEffect(() => {
		// Add event listener for beforeunload to warn about unsaved changes
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [project, user, handleBeforeUnload]);

	// ----------------------
	// Tour management
	// ----------------------

	const updatePreTourChecklist = useCallback((key: string, ready: boolean) => {
		setPreTourCheckList((prevChecklist) => {
			const existingItemIndex = prevChecklist.findIndex(item => item.key === key);

			if (existingItemIndex !== -1) {
				const existingItem = prevChecklist[existingItemIndex];

				// If nothing actually changes, return the same array to avoid a re-render
				if (existingItem.ready === ready) {
					return prevChecklist;
				}

				const updatedChecklist = [...prevChecklist];
				updatedChecklist[existingItemIndex] = { key, ready };
				return updatedChecklist;
			}

			// New item
			return [...prevChecklist, { key, ready }];
		});
	}, []);

	const startBeginnerTour = useCallback(async () => {
		// Load the tour project template if we don't have a project yet
		if ((project == null || !project.hasItems()) && user !== null) {
			// Use the imported template data to restore a project
			const templateProject = await Project.createNew(user);
			if (tourProjectTemplate) {
				const tracks = tourProjectTemplate.tracks.map((track: any) => ProjectTrack.fromJson(track));
				templateProject.loadTracks(tracks);
				templateProject.tracks.forEach((track) => track.sortItems());

				const tempoChangeEvents = tourProjectTemplate.tempoChanges?.map((tempoChangeData: { measureNumber: number, tempo: any }) => {
					return {
						measureNumber: tempoChangeData.measureNumber,
						tempoChange: TempoChange.fromJson(tempoChangeData.tempo)
					}
				}) ?? [];
				templateProject.loadTempoChanges(tempoChangeEvents);
			}
			setProject(templateProject);
		}

		setBeginnerTourActive(true);
		jumpToTourStep(0);
		setBeginnerTourStartModalOpen(false);
	}, [project, user]);

	useEffect(() => {
		// If the pretour checklist has atleast one item and all pretour checklist items are ready, set beginneringTourActive true
		if (preTourChecklist.length > 0 && preTourChecklist.every(item => item.ready)) {
			setBeginnerTourStartModalOpen(true);
		} else {
			setBeginnerTourStartModalOpen(false);
			setBeginnerTourActive(false);
		}
	}, [preTourChecklist]);

	const handleGetHelpers = (helpers: StoreHelpers) => {
		tourHelpersRef.current = helpers;
	};

	const moveToNextTourStep = () => {
		if (tourHelpersRef.current) {
			tourHelpersRef.current.next();
		}
	}

	const moveToPrevTourStep = () => {
		if (tourHelpersRef.current) {
			tourHelpersRef.current.prev();
		}
	}

	const jumpToTourStep = (index: number) => {
		if (tourHelpersRef.current) {
			tourHelpersRef.current.go(index);
		}
	};

	// Callback to handle tour events (e.g., finish or skip)
	const handleJoyrideCallback = (data: CallBackProps) => {
		const { status, index } = data;
		setCurrentTourStep(index);
		const tutorialFinished = status === STATUS.FINISHED;
		const tutorialSkipped = status === STATUS.SKIPPED;
		if (tutorialFinished || tutorialSkipped) {
			updatePreTourChecklist("tour-not-completed-this-session", false); // This is how we stop the tour for this session.
			if (tutorialFinished) {
				confettiTime();
			}
			if (user !== null && token !== null && !user.roles.includes("Guest")) {
				// If the tutorial was actually completed or if the tutorial was skipped and the user has a project saved, we cam set the completion status true
				if (tutorialFinished || (tutorialSkipped && user.projectIds.length > 0)) {
					ConductionAPI.setTutorialCompletionStatus(user.id, true, token)
				}
			}
		}
	};

	// ----------------------
	// Bells and whistles
	// ----------------------
	const [showConfetti, setShowConfetti] = useState(false);

	const confettiTime = () => {
		setShowConfetti(true);
	}

	return (
		<AuthContext.Provider
			value={{
				user,
				token,
				songs,
				project,
				loading,
				loadingPercent,
				login,
				guestLogin,
				logout,
				profileAvatar,
				fetchProfileAvatar,
				roleCheck,
				loginModalState,
				updateProjectName,
				saveProject,
				openProject,
				saveStatus,
				updateProject,
				lockProject,
				updatePreTourChecklist,
				startBeginnerTour,
				beginnerTourStartModalOpen,
				setBeginnerTourStartModalOpen,
				beginnerTourActive,
				currentTourStep,
				moveToNextTourStep,
				moveToPrevTourStep,
				jumpToTourStep,
				classroom,
				participatingLiveEvents: participatingLiveEvents ?? [],
				administratingLiveEvents: administratingLiveEvents ?? [],
				playingLiveEvents: playingLiveEvents ?? [],
				activeParticipatingLiveEvent,
				activeAdministratingLiveEvent,
				activePlayingLiveEvent,
				updateLiveEvents,
				currentTos,
				fetchCurrentTos,
				agreeToTos
			}}
		>
			{/* React Joyride component for our tour */}
			<Joyride
				steps={tourSteps}
				run={beginnerTourActive}
				disableCloseOnEsc
				disableOverlayClose
				disableScrolling
				disableScrollParentFix
				continuous
				callback={handleJoyrideCallback}
				getHelpers={handleGetHelpers}
				styles={{
					options: {
						primaryColor: (theme.colors?.red)?.[6] // Custom color for the next button
					},
				}}
			/>

			{showConfetti &&
				<Confetti
					numberOfPieces={200}
					recycle={false}
					onConfettiComplete={() => setShowConfetti(false)}
				/>
			}

			{children}
		</AuthContext.Provider>
	);
};